import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getHeaderTickerConfig,
  listPublicFarmerReports,
  listPublicPosts,
  setHeaderTickerConfig,
} from '../lib/firestore';

function normalizeTickerConfig(config) {
  return {
    enabled: config?.enabled ?? true,
    operatorPostIds: Array.isArray(config?.operatorPostIds) ? config.operatorPostIds : [],
    farmerReportIds: Array.isArray(config?.farmerReportIds) ? config.farmerReportIds : [],
    operatorDisplayCount: Number(config?.operatorDisplayCount) || 0,
    farmerDisplayCount: Number(config?.farmerDisplayCount) || 0,
    operatorSelectionMode: config?.operatorSelectionMode === 'random' ? 'random' : 'fixed',
    farmerSelectionMode: config?.farmerSelectionMode === 'fixed' ? 'fixed' : 'random',
    priority: config?.priority === 'farmer' ? 'farmer' : 'operator',
  };
}

export default function AdminHeaderTickerPage() {
  const { user, isAdmin, isStaff } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [config, setConfig] = useState(normalizeTickerConfig());
  const [posts, setPosts] = useState([]);
  const [reports, setReports] = useState([]);

  const roleLabel = isAdmin ? 'サイト管理者' : '運営';

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [savedConfig, publicPosts, publicReports] = await Promise.all([
          getHeaderTickerConfig(),
          listPublicPosts(),
          listPublicFarmerReports(),
        ]);
        const normalizedConfig = normalizeTickerConfig(savedConfig);
        setConfig(normalizedConfig);
        setPosts(publicPosts);
        setReports(publicReports);
        if (isAdmin && user?.uid) {
          try {
            await setHeaderTickerConfig({
              enabled: normalizedConfig.enabled,
              operatorPostIds: normalizedConfig.operatorPostIds,
              farmerReportIds: normalizedConfig.farmerReportIds,
              operatorDisplayCount: normalizedConfig.operatorDisplayCount,
              farmerDisplayCount: normalizedConfig.farmerDisplayCount,
              operatorSelectionMode: normalizedConfig.operatorSelectionMode,
              farmerSelectionMode: normalizedConfig.farmerSelectionMode,
              priority: normalizedConfig.priority,
            }, user.uid);
          } catch (error) {
            console.warn('header ticker public sync failed', error);
          }
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isAdmin, user?.uid]);

  const selectedPosts = useMemo(
    () => config.operatorPostIds.map((id) => posts.find((row) => row.id === id)).filter(Boolean),
    [config.operatorPostIds, posts]
  );
  const selectedReports = useMemo(
    () => config.farmerReportIds.map((id) => reports.find((row) => row.id === id)).filter(Boolean),
    [config.farmerReportIds, reports]
  );
  const postCandidates = useMemo(
    () => [...posts].sort((a, b) => String(b.publishedDate || '').localeCompare(String(a.publishedDate || ''))),
    [posts]
  );
  const reportCandidates = useMemo(
    () => [...reports].sort((a, b) => String(b.publishedDate || b.month || '').localeCompare(String(a.publishedDate || a.month || ''))),
    [reports]
  );
  const tickerPreviewItems = useMemo(() => {
    const operatorItems = selectedPosts
      .slice(0, Math.max(0, Number(config.operatorDisplayCount) || 0))
      .map((post) => `運営 | ${post.title || '(無題)'}`);
    const farmerItems = selectedReports
      .slice(0, Math.max(0, Number(config.farmerDisplayCount) || 0))
      .map((report) => `${report.farmerName || '農家'} | ${report.title || report.highlights || '(無題)'}`);

    return config.priority === 'farmer'
      ? [...farmerItems, ...operatorItems]
      : [...operatorItems, ...farmerItems];
  }, [config.farmerDisplayCount, config.operatorDisplayCount, config.priority, selectedPosts, selectedReports]);

  function toggleId(field, id) {
    setConfig((prev) => ({
      ...prev,
      [field]: prev[field].includes(id)
        ? prev[field].filter((value) => value !== id)
        : [...prev[field], id],
    }));
  }

  async function handleSave(event) {
    event.preventDefault();
    setFeedback({ type: '', message: '' });
    if (!isAdmin) {
      setFeedback({ type: 'error', message: 'この操作はサイト管理者のみ実行できます。' });
      return;
    }

    setSaving(true);
    try {
      await setHeaderTickerConfig({
        enabled: config.enabled,
        operatorPostIds: config.operatorPostIds,
        farmerReportIds: config.farmerReportIds,
        operatorDisplayCount: Math.max(0, Number(config.operatorDisplayCount) || 0),
        farmerDisplayCount: Math.max(0, Number(config.farmerDisplayCount) || 0),
        operatorSelectionMode: config.operatorSelectionMode,
        farmerSelectionMode: config.farmerSelectionMode,
        priority: config.priority,
      }, user.uid);
      setFeedback({ type: 'success', message: 'ヘッダー記事の設定を保存しました。' });
    } catch {
      setFeedback({ type: 'error', message: 'ヘッダー記事の保存に失敗しました。' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Helmet><title>Drs.Farm | ヘッダー記事</title></Helmet>
      <section className="panel header-ticker-hero">
        <div className="header-ticker-hero-copy">
          <p className="header-ticker-kicker">Header News</p>
          <h1>ヘッダー記事</h1>
          <p className="hint">ログイン権限: {roleLabel}</p>
        </div>
        <div className="row-actions">
          <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
        </div>
      </section>

      <section className="admin-grid header-ticker-top-grid">
        <article className="panel form-grid header-ticker-settings-panel">
          <div className="header-ticker-panel-head">
            <div>
              <p className="header-ticker-panel-kicker">SETTINGS</p>
              <h2>表示設定</h2>
            </div>
          </div>
          {loading ? <p>読み込み中...</p> : (
            <form className="form-grid" onSubmit={handleSave}>
              <div className="header-ticker-settings-grid">
                <label className="checkbox-row header-ticker-toggle">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
                  />
                  <span>ヘッダー記事を表示する</span>
                </label>

                <label>表示優先
                  <select value={config.priority} onChange={(e) => setConfig((prev) => ({ ...prev, priority: e.target.value }))}>
                    <option value="operator">運営記事優先</option>
                    <option value="farmer">農家近況優先</option>
                  </select>
                </label>

                <label>運営記事の表示数
                  <input
                    type="number"
                    min="0"
                    value={config.operatorDisplayCount}
                    onChange={(e) => setConfig((prev) => ({ ...prev, operatorDisplayCount: e.target.value }))}
                  />
                </label>

                <label>運営記事の選び方
                  <select value={config.operatorSelectionMode} onChange={(e) => setConfig((prev) => ({ ...prev, operatorSelectionMode: e.target.value }))}>
                    <option value="fixed">選択順で固定表示</option>
                    <option value="random">選択記事からランダム表示</option>
                  </select>
                </label>

                <label>農家近況の表示数
                  <input
                    type="number"
                    min="0"
                    value={config.farmerDisplayCount}
                    onChange={(e) => setConfig((prev) => ({ ...prev, farmerDisplayCount: e.target.value }))}
                  />
                </label>

                <label>農家近況の選び方
                  <select value={config.farmerSelectionMode} onChange={(e) => setConfig((prev) => ({ ...prev, farmerSelectionMode: e.target.value }))}>
                    <option value="random">選択記事からランダム表示</option>
                    <option value="fixed">選択順で固定表示</option>
                  </select>
                </label>
              </div>

              <div className="row-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '保存中...' : '設定を保存'}
                </button>
              </div>
              {feedback.message ? <p className={feedback.type === 'success' ? 'success-text' : 'error-text'}>{feedback.message}</p> : null}
            </form>
          )}
        </article>

        <article className="panel form-grid header-ticker-summary-panel">
          <div className="header-ticker-panel-head">
            <div>
              <p className="header-ticker-panel-kicker">SUMMARY</p>
              <h2>選択中の投稿</h2>
            </div>
          </div>
          <div className="header-ticker-metrics">
            <article>
              <strong>{selectedPosts.length}</strong>
              <span>運営記事</span>
            </article>
            <article>
              <strong>{selectedReports.length}</strong>
              <span>農家近況</span>
            </article>
            <article>
              <strong>{tickerPreviewItems.length}</strong>
              <span>ヘッダー表示数</span>
            </article>
          </div>
          <div className="header-ticker-preview-box">
            <p className="header-ticker-preview-label">ヘッダープレビュー</p>
            {tickerPreviewItems.length ? (
              <div className="header-ticker-preview-track">
                {tickerPreviewItems.map((item) => (
                  <span key={item} className="header-ticker-preview-pill">{item}</span>
                ))}
              </div>
            ) : (
              <p className="hint">現在はヘッダーに表示される記事がありません。</p>
            )}
          </div>
          {selectedPosts.length > 0 ? (
            <div className="header-ticker-selected-block">
              <h3>運営記事</h3>
              <div className="tag-row">
                {selectedPosts.map((post) => (
                  <button key={post.id} type="button" className="pill" onClick={() => toggleId('operatorPostIds', post.id)}>
                    {post.title} ×
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {selectedReports.length > 0 ? (
            <div className="header-ticker-selected-block">
              <h3>農家近況報告</h3>
              <div className="tag-row">
                {selectedReports.map((report) => (
                  <button key={report.id} type="button" className="pill" onClick={() => toggleId('farmerReportIds', report.id)}>
                    {(report.farmerName || '農家')} / {report.title || report.highlights || report.month || '近況報告'} ×
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </article>
      </section>

      <section className="admin-grid header-ticker-candidate-grid">
        <article className="panel header-ticker-candidate-panel">
          <div className="header-ticker-panel-head">
            <div>
              <p className="header-ticker-panel-kicker">OPERATOR POSTS</p>
              <h2>運営記事候補</h2>
            </div>
            <p className="directory-count">{postCandidates.length}件</p>
          </div>
          <ul className="directory-list">
            {postCandidates.map((post) => (
              <li key={post.id} className={`directory-row header-ticker-row ${config.operatorPostIds.includes(post.id) ? 'is-selected' : ''}`}>
                <label className="ticker-select-row header-ticker-select-row">
                  <input
                    type="checkbox"
                    checked={config.operatorPostIds.includes(post.id)}
                    onChange={() => toggleId('operatorPostIds', post.id)}
                  />
                  <span>
                    <strong>{post.title || '(無題)'}</strong>
                    <span className="directory-meta">{post.publishedDate || '-'}</span>
                    <span className="directory-meta">{post.body || '-'}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </article>

        <article className="panel header-ticker-candidate-panel">
          <div className="header-ticker-panel-head">
            <div>
              <p className="header-ticker-panel-kicker">FARMER REPORTS</p>
              <h2>農家近況報告候補</h2>
            </div>
            <p className="directory-count">{reportCandidates.length}件</p>
          </div>
          <ul className="directory-list">
            {reportCandidates.map((report) => (
              <li key={`${report.farmerId}-${report.id}`} className={`directory-row header-ticker-row ${config.farmerReportIds.includes(report.id) ? 'is-selected' : ''}`}>
                <label className="ticker-select-row header-ticker-select-row">
                  <input
                    type="checkbox"
                    checked={config.farmerReportIds.includes(report.id)}
                    onChange={() => toggleId('farmerReportIds', report.id)}
                  />
                  <span>
                    <strong>{report.farmerName || '農家'} / {report.title || report.highlights || '(無題)'}</strong>
                    <span className="directory-meta">{report.publishedDate || report.month || '-'}</span>
                    <span className="directory-meta">タグ: {(report.tags || report.cropTags || []).join(' / ') || '-'}</span>
                    <span className="directory-meta">{report.body || '-'}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </article>
      </section>
    </>
  );
}
